import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FiBarChart2,
  FiCalendar,
  FiFileText,
  FiRefreshCw,
  FiTrendingUp,
} from "react-icons/fi";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "../Components/PmsThemeContext";
import useCrossTabDataRefreshToken from "../hooks/useCrossTabDataRefresh";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";

const PAGE_SIZE = 500;
const DAY_MS = 24 * 60 * 60 * 1000;

const REPORT_MESSAGES = {
  en: {
    title: "Monthly Stay Performance",
    subtitle: "All stay months with data up to today for the selected business unit.",
    updated: "Updated:",
    refresh: "Refresh",
    states: {
      noPermission: "You do not have permission to view reports.",
      loading: "Loading report data...",
      loadErrorTitle: "Could not load the report.",
    },
    messages: {
      reservationsLoadError: "Could not load reservations for the report.",
      loadError: "Could not load the report.",
    },
    cards: {
      reservations: "Reservations",
      roomNights: "Total room nights",
      totalPrice: "Total price",
      averageOcc: "Average OCC",
      adr: "ADR",
      revpar: "RevPAR",
      monthsWithData: "Months with data",
      stayedNights: "Stayed nights",
      monthlyAverage: "Monthly average",
    },
    table: {
      month: "Month",
      externalAgency: "Agency",
      reservations: "Reservations",
      avgLos: "Avg LOS",
      roomNights: "Total room nights",
      totalPrice: "Total price",
      occupancy: "OCC",
      adr: "ADR",
      revpar: "RevPAR",
      total: "Total",
    },
    panels: {
      externalAgencyPerformance: "Monthly Agency Performance",
    },
    fallback: {
      directHotel: "Direct hotel",
    },
    empty: "No reservations stayed in this period.",
  },
  es: {
    title: "Rendimiento mensual de estancias",
    subtitle: "Todos los meses con datos de estancia hasta hoy para la unidad de negocio seleccionada.",
    updated: "Actualizado:",
    refresh: "Actualizar",
    states: {
      noPermission: "No tienes permisos para ver reportes.",
      loading: "Cargando datos del reporte...",
      loadErrorTitle: "No se ha podido cargar el reporte.",
    },
    messages: {
      reservationsLoadError: "No se han podido cargar las reservas para el reporte.",
      loadError: "No se ha podido cargar el reporte.",
    },
    cards: {
      reservations: "Reservas",
      roomNights: "Room nights totales",
      totalPrice: "Precio total",
      averageOcc: "OCC media",
      adr: "ADR",
      revpar: "RevPAR",
      monthsWithData: "Meses con datos",
      stayedNights: "Noches alojadas",
      monthlyAverage: "Media mensual",
    },
    table: {
      month: "Mes",
      externalAgency: "Agencia",
      reservations: "Reservas",
      avgLos: "LOS medio",
      roomNights: "Room nights totales",
      totalPrice: "Precio total",
      occupancy: "OCC",
      adr: "ADR",
      revpar: "RevPAR",
      total: "Total",
    },
    panels: {
      externalAgencyPerformance: "Rendimiento mensual por agencia",
    },
    fallback: {
      directHotel: "Directa hotel",
    },
    empty: "No hay reservas alojadas en este periodo.",
  },
  ca: {
    title: "Rendiment mensual d'estades",
    subtitle: "Tots els mesos amb dades d'estada fins avui per a la unitat de negoci seleccionada.",
    updated: "Actualitzat:",
    refresh: "Actualitza",
    states: {
      noPermission: "No tens permisos per veure informes.",
      loading: "Carregant dades de l'informe...",
      loadErrorTitle: "No s'ha pogut carregar l'informe.",
    },
    messages: {
      reservationsLoadError: "No s'han pogut carregar les reserves per a l'informe.",
      loadError: "No s'ha pogut carregar l'informe.",
    },
    cards: {
      reservations: "Reserves",
      roomNights: "Room nights totals",
      totalPrice: "Preu total",
      averageOcc: "OCC mitjana",
      adr: "ADR",
      revpar: "RevPAR",
      monthsWithData: "Mesos amb dades",
      stayedNights: "Nits allotjades",
      monthlyAverage: "Mitjana mensual",
    },
    table: {
      month: "Mes",
      externalAgency: "Agencia",
      reservations: "Reserves",
      avgLos: "LOS mitja",
      roomNights: "Room nights totals",
      totalPrice: "Preu total",
      occupancy: "OCC",
      adr: "ADR",
      revpar: "RevPAR",
      total: "Total",
    },
    panels: {
      externalAgencyPerformance: "Rendiment mensual per agencia",
    },
    fallback: {
      directHotel: "Directa hotel",
    },
    empty: "No hi ha reserves allotjades en aquest periode.",
  },
};

function toIsoDateLocal(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeDate(value) {
  if (!value) return "";
  if (value instanceof Date) return toIsoDateLocal(value);
  return String(value).slice(0, 10);
}

function parseDateOnly(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function addDays(iso, days) {
  const dt = parseDateOnly(iso);
  if (!dt) return iso;
  dt.setDate(dt.getDate() + days);
  return toIsoDateLocal(dt);
}

function startOfMonthIso(date) {
  return toIsoDateLocal(new Date(date.getFullYear(), date.getMonth(), 1));
}

function endOfMonthIso(date) {
  return toIsoDateLocal(new Date(date.getFullYear(), date.getMonth() + 1, 0));
}

function daysInMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function firstDayOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function capacityDaysForMonth(date, asOfDate) {
  const fullMonthDays = daysInMonth(date);
  if (
    asOfDate &&
    date.getFullYear() === asOfDate.getFullYear() &&
    date.getMonth() === asOfDate.getMonth()
  ) {
    return Math.min(fullMonthDays, asOfDate.getDate());
  }
  return fullMonthDays;
}

function minIsoDate(a, b) {
  if (!a) return b || "";
  if (!b) return a || "";
  return a <= b ? a : b;
}

function nightsDiff(checkIn, checkOut) {
  const a = parseDateOnly(normalizeDate(checkIn));
  const b = parseDateOnly(normalizeDate(checkOut));
  if (!a || !b) return 0;
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / DAY_MS));
}

function overlapNights(checkIn, checkOut, fromIso, toIso) {
  const stayStart = parseDateOnly(normalizeDate(checkIn));
  const stayEnd = parseDateOnly(normalizeDate(checkOut));
  const rangeStart = parseDateOnly(fromIso);
  const rangeEnd = parseDateOnly(addDays(toIso, 1));
  if (!stayStart || !stayEnd || !rangeStart || !rangeEnd || stayEnd <= stayStart) return 0;
  const overlapStart = Math.max(stayStart.getTime(), rangeStart.getTime());
  const overlapEnd = Math.min(stayEnd.getTime(), rangeEnd.getTime());
  if (overlapEnd <= overlapStart) return 0;
  return Math.round((overlapEnd - overlapStart) / DAY_MS);
}

function isCancelled(status) {
  const s = String(status || "").trim().toLowerCase();
  return s === "cancelled" || s === "canceled";
}

function isNoShow(status) {
  return String(status || "").trim().toLowerCase() === "no show";
}

function isOperational(status) {
  const s = String(status || "").trim().toLowerCase();
  if (!s) return true;
  if (s === "draft" || s === "requested") return false;
  return !isCancelled(s) && !isNoShow(s);
}

function toNum(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toRevenue(reservation) {
  const base = toNum(reservation?.total_price);
  const extras = toNum(reservation?.reservation_extras_total ?? reservation?.extras_total);
  return base + extras;
}

function reservationNights(reservation) {
  const n = toNum(reservation?.room_nights);
  if (n > 0) return Math.round(n);
  return nightsDiff(reservation?.check_in_date, reservation?.check_out_date);
}

function reservationCheckOut(reservation) {
  const direct = normalizeDate(reservation?.check_out_date);
  if (direct) return direct;
  const checkIn = normalizeDate(reservation?.check_in_date);
  const nights = reservationNights(reservation);
  return checkIn && nights > 0 ? addDays(checkIn, nights) : "";
}

function rowsFromPayload(payload) {
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload?.rows) ? payload.rows : [];
}

async function fetchAllReservations(businessUnitId, signal) {
  const first = await axios.get(
    `/api/reservations?business_unit_id=${businessUnitId}&page=1&page_size=${PAGE_SIZE}&show_cancelled=1`,
    { signal }
  );
  const firstRows = rowsFromPayload(first?.data);
  const totalPages = Array.isArray(first?.data)
    ? 1
    : Math.max(1, Number(first?.data?.total_pages || 1));
  if (totalPages <= 1) return firstRows;
  const all = [...firstRows];
  for (let page = 2; page <= totalPages; page += 1) {
    const next = await axios.get(
      `/api/reservations?business_unit_id=${businessUnitId}&page=${page}&page_size=${PAGE_SIZE}&show_cancelled=1`,
      { signal }
    );
    all.push(...rowsFromPayload(next?.data));
  }
  return all;
}

function externalAgencyLabel(reservation, fallbackLabel) {
  const agency = String(reservation?.external_agency_name || "").trim();
  if (!agency || agency === "-" || agency === "—") return fallbackLabel;
  const comparable = agency.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (comparable === "bookingcom") return "Booking.com";
  if (comparable === "logismanager" || comparable === "pilcorebookingengine") {
    return "Booking Engine";
  }
  return agency;
}

function makeEmptyMonth(date, totalRooms, locale, asOfDate) {
  const start = startOfMonthIso(date);
  const end = endOfMonthIso(date);
  const capacity = totalRooms * capacityDaysForMonth(date, asOfDate);

  return {
    key: monthKey(date),
    start,
    end,
    label: date.toLocaleDateString(locale, { month: "short", year: "numeric" }),
    sortDate: new Date(date.getFullYear(), date.getMonth(), 1),
    capacity,
    reservations: 0,
    roomNights: 0,
    totalLosNights: 0,
    totalPrice: 0,
    occupancyPct: 0,
    avgLos: 0,
    adr: 0,
    revpar: 0,
    agencies: new Map(),
  };
}

const uiCss = `
  .rp-page { width: 100%; padding: 0 16px 14px; height: calc(100vh - 28px); }
  .rp-shell {
    position: relative;
    height: 100%;
    border-radius: 18px;
    border: 1px solid transparent;
    background:
      linear-gradient(180deg, rgba(25, 25, 51, 0.94), rgba(10, 10, 22, 0.94)) padding-box,
      linear-gradient(135deg, rgba(48, 216, 255, 0.55), rgba(37, 154, 211, 0.5), rgba(255, 77, 79, 0.45)) border-box;
    box-shadow: 0 28px 90px rgba(0, 0, 0, 0.56);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .rp-shell::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(900px 300px at 10% 0%, rgba(48, 216, 255, 0.14), transparent 60%),
      radial-gradient(700px 260px at 90% 10%, rgba(37, 154, 211, 0.11), transparent 62%),
      radial-gradient(800px 260px at 40% 110%, rgba(255, 77, 79, 0.1), transparent 60%);
    pointer-events: none;
    opacity: 0.95;
  }
  .rp-shell > * { position: relative; z-index: 1; }
  .rp-header { padding: 16px 16px 14px; border-bottom: 1px solid rgba(255,255,255,0.08); display: grid; grid-template-columns: 1fr auto; gap: 10px 14px; align-items: end; }
  .rp-title {
    margin: 0;
    font-size: 1.35rem;
    font-weight: 900;
    color: transparent;
    background: linear-gradient(90deg, #e6e6f7 0%, rgba(48, 216, 255, 0.96) 42%, rgba(37, 154, 211, 0.92) 92%);
    -webkit-background-clip: text;
    background-clip: text;
    line-height: 1.16;
  }
  .rp-sub { margin-top: 4px; font-size: 0.82rem; color: rgba(230,230,247,0.7); }
  .rp-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; justify-content: flex-end; }
  .rp-pill { border: 1px solid rgba(255,255,255,0.16); border-radius: 999px; padding: 6px 10px; font-size: 0.74rem; color: rgba(230,230,247,0.82); background: rgba(255,255,255,0.04); }
  .rp-btn { border: 1px solid rgba(48,216,255,0.35); border-radius: 11px; padding: 8px 11px; font-size: 0.78rem; font-weight: 800; color: #c9f1ff; background: rgba(48,216,255,0.14); cursor: pointer; display: inline-flex; gap: 6px; align-items: center; transition: transform 120ms ease, box-shadow 120ms ease; }
  .rp-btn:hover { transform: translateY(-1px); box-shadow: 0 14px 22px rgba(0,0,0,0.28); }
  .rp-body { padding: 12px; display: flex; flex-direction: column; gap: 10px; overflow: auto; min-height: 0; }
  .rp-state { min-height: 280px; display: flex; flex-direction: column; justify-content: center; align-items: center; color: rgba(230,230,247,0.82); gap: 8px; }
  .rp-spin { width: 20px; height: 20px; border: 2px solid rgba(255,255,255,0.22); border-top-color: #30d8ff; border-radius: 999px; animation: rp-spin 0.8s linear infinite; }
  @keyframes rp-spin { to { transform: rotate(360deg); } }
  .rp-cards { display: grid; grid-template-columns: repeat(5, minmax(0,1fr)); gap: 8px; }
  .rp-card { border: 1px solid rgba(255,255,255,0.1); border-radius: 13px; background: linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02)); padding: 10px; min-width: 0; }
  .rp-cardHead { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
  .rp-cardLabel { font-size: 0.73rem; font-weight: 800; text-transform: uppercase; color: rgba(230,230,247,0.74); }
  .rp-cardValue { margin-top: 7px; font-size: 1.28rem; font-weight: 900; color: rgba(240,245,255,0.96); line-height: 1.14; }
  .rp-cardHint { margin-top: 4px; font-size: 0.74rem; color: rgba(230,230,247,0.62); }
  .rp-panel {
    border: 1px solid rgba(88, 187, 255, 0.34);
    border-radius: 13px;
    background:
      linear-gradient(180deg, rgba(20, 35, 58, 0.9), rgba(14, 25, 43, 0.92)) padding-box,
      linear-gradient(135deg, rgba(88, 187, 255, 0.45), rgba(36, 161, 225, 0.35), rgba(106, 221, 255, 0.36)) border-box;
    overflow: hidden;
  }
  .rp-panelHeader { padding: 11px 12px 10px; border-bottom: 1px solid rgba(148, 214, 255, 0.28); background: linear-gradient(180deg, rgba(43, 79, 126, 0.94), rgba(31, 60, 103, 0.92)); }
  .rp-panelTitle { margin: 0; padding: 0; font-size: 0.92rem; font-weight: 900; color: rgba(240,248,255,0.96); display: flex; align-items: center; gap: 7px; }
  .rp-tableWrap { overflow: auto; max-height: calc(100vh - 270px); }
  .rp-table { width: 100%; min-width: 900px; border-collapse: separate; border-spacing: 0; font-size: 0.76rem; color: rgba(230,230,247,0.86); }
  .rp-table th { position: sticky; top: 0; z-index: 1; text-align: right; padding: 9px 10px; color: rgba(226, 246, 255, 0.92); background: rgba(17, 35, 59, 0.98); border-bottom: 1px solid rgba(148, 214, 255, 0.18); font-weight: 900; white-space: nowrap; }
  .rp-table th:first-child,
  .rp-table td:first-child { text-align: left; position: sticky; left: 0; z-index: 2; }
  .rp-table th:first-child { background: rgba(17, 35, 59, 1); }
  .rp-table td:first-child { background: rgba(16, 30, 50, 0.98); }
  .rp-table td { padding: 9px 10px; border-bottom: 1px solid rgba(255,255,255,0.055); text-align: right; white-space: nowrap; }
  .rp-table tbody tr:hover td { background: rgba(48,216,255,0.055); }
  .rp-month { display: flex; align-items: center; gap: 8px; color: rgba(241,247,255,0.96); font-weight: 850; }
  .rp-monthDot { width: 7px; height: 7px; border-radius: 999px; background: #30d8ff; box-shadow: 0 0 18px rgba(48,216,255,0.55); flex: 0 0 auto; }
  .rp-kicker { color: rgba(235, 248, 255, 0.98); font-weight: 900; }
  .rp-cellSub { margin-left: 5px; color: rgba(178, 205, 221, 0.74); font-size: 0.69rem; font-weight: 750; }
  .rp-empty { padding: 24px 12px; text-align: center; color: rgba(230,230,247,0.68); }
  .rp-monthAgencyPanel {
    border: 1px solid transparent;
    background:
      linear-gradient(180deg, rgba(15, 23, 42, 0.97), rgba(8, 13, 24, 0.98)) padding-box,
      linear-gradient(135deg, rgba(100, 116, 139, 0.28), rgba(51, 65, 85, 0.22)) border-box;
    box-shadow: 0 22px 54px rgba(0, 0, 0, 0.34);
    padding-bottom: 12px;
  }
  .rp-monthAgencyPanel .rp-panelHeader {
    background: rgba(15, 23, 42, 0.92);
    border-bottom: 1px solid rgba(148, 163, 184, 0.12);
    margin-bottom: 8px;
  }
  .rp-monthAgencyPanel .rp-panelTitle { color: rgba(247, 250, 252, 0.96); }
  .rp-monthAgencyPanel .rp-tableWrap {
    margin: 0 12px;
    padding: 0;
    box-sizing: border-box;
    border-radius: 10px;
    max-height: calc(100vh - 292px);
    overflow: auto;
    scrollbar-gutter: stable;
    scrollbar-width: thin;
    scrollbar-color: rgba(109, 208, 235, 0.72) rgba(255, 255, 255, 0.08);
  }
  .rp-body::-webkit-scrollbar-button,
  .rp-monthAgencyPanel .rp-tableWrap::-webkit-scrollbar-button {
    display: none;
    width: 0;
    height: 0;
  }
  .rp-monthAgencyPanel .rp-tableWrap::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  .rp-monthAgencyPanel .rp-tableWrap::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.08);
    border-radius: 999px;
  }
  .rp-monthAgencyPanel .rp-tableWrap::-webkit-scrollbar-thumb {
    background: rgba(109, 208, 235, 0.72);
    border: 2px solid rgba(8, 13, 24, 0.94);
    border-radius: 999px;
  }
  .rp-monthAgencyPanel .rp-tableWrap::-webkit-scrollbar-corner {
    background: transparent;
  }
  .rp-monthAgencyTable {
    --rp-agency-row-bg: #1b2029;
    min-width: 1040px;
    border-collapse: separate;
    border-spacing: 0 5px;
    color: rgba(226, 232, 240, 0.88);
    background: transparent;
    font-size: 0.76rem;
  }
  .rp-monthAgencyTable th {
    position: sticky;
    top: 0;
    z-index: 3;
    color: rgba(226, 232, 240, 0.86);
    background: rgba(15, 23, 42, 0.98);
    border: 0;
    border-bottom: 1px solid rgba(148, 163, 184, 0.16);
    padding: 9px 10px;
    font-size: 0.64rem;
    font-weight: 850;
    letter-spacing: 0;
    text-transform: uppercase;
    text-align: right;
    white-space: nowrap;
  }
  .rp-monthAgencyTable th:first-child,
  .rp-monthAgencyTable td:first-child {
    position: static;
    left: auto;
  }
  .rp-monthAgencyTable th:first-child,
  .rp-monthAgencyTable th:nth-child(2) {
    text-align: left;
  }
  .rp-monthAgencyTable td {
    padding: 8px 10px;
    border: 0;
    border-top: 1px solid rgba(148, 163, 184, 0.075);
    border-bottom: 1px solid rgba(15, 23, 42, 0.42);
    background: rgba(30, 41, 59, 0.36);
    text-align: right;
    white-space: nowrap;
  }
  .rp-agencyDataRow td {
    padding-top: 7px;
    padding-bottom: 7px;
    font-size: 0.72rem;
    color: rgba(203, 213, 225, 0.80);
  }
  .rp-monthAgencyTable .rp-agencyDataRow,
  .rp-monthAgencyTable .rp-agencyDataRow:nth-child(odd),
  .rp-monthAgencyTable .rp-agencyDataRow:nth-child(even),
  .rp-monthAgencyTable .rp-agencyDataRow:hover {
    background: var(--rp-agency-row-bg) !important;
  }
  .rp-monthAgencyTable .rp-agencyDataRow > td:not(.rp-monthBlockCell),
  .rp-monthAgencyTable .rp-agencyDataRow:nth-child(odd) > td:not(.rp-monthBlockCell),
  .rp-monthAgencyTable .rp-agencyDataRow:nth-child(even) > td:not(.rp-monthBlockCell),
  .rp-monthAgencyTable .rp-agencyDataRow:hover > td:not(.rp-monthBlockCell),
  .rp-monthAgencyTable .rp-agencyDataRow > td:first-child:not(.rp-monthBlockCell),
  .rp-monthAgencyTable .rp-agencyDataRow > td:last-child:not(.rp-monthBlockCell) {
    background: var(--rp-agency-row-bg) !important;
  }
  .rp-monthBlockCell {
    min-width: 96px;
    vertical-align: middle;
    text-align: center !important;
    background: rgba(30, 41, 59, 0.78) !important;
    border-left: 1px solid rgba(148, 163, 184, 0.18) !important;
    border-right: 1px solid rgba(148, 163, 184, 0.10) !important;
    border-radius: 8px 0 0 8px;
  }
  .rp-monthBlockLabel {
    display: inline-block;
    font-size: 0.88rem;
    font-weight: 850;
    color: rgba(241, 245, 249, 0.94);
  }
  .rp-agencyCell {
    min-width: 280px;
    text-align: left !important;
    font-weight: 650;
    color: rgba(226, 232, 240, 0.82);
  }
  .rp-agencyDataRow .rp-agencyCell {
    font-size: 0.73rem;
    font-weight: 650;
    color: rgba(226, 232, 240, 0.84);
  }
  .rp-monthTotalRow td {
    background: rgba(51, 65, 85, 0.72) !important;
    color: rgba(248, 250, 252, 0.96);
    font-size: 0.78rem;
    font-weight: 850;
    border-top: 1px solid rgba(148, 163, 184, 0.24);
    border-bottom: 1px solid rgba(148, 163, 184, 0.18);
  }
  .rp-monthTotalRow .rp-agencyCell {
    letter-spacing: 0;
    color: rgba(248, 250, 252, 0.96);
  }

  @media (max-width: 1100px) {
    .rp-header { grid-template-columns: 1fr; }
    .rp-actions { justify-content: flex-start; }
    .rp-cards { grid-template-columns: repeat(2, minmax(0,1fr)); }
  }

  @media (max-width: 620px) {
    .rp-page { padding: 0 8px 10px; }
    .rp-cards { grid-template-columns: 1fr; }
  }
`;

const lightCss = `
  .rp-shell {
    background:
      linear-gradient(180deg, rgba(252, 249, 243, 0.97), rgba(240, 234, 224, 0.97)) padding-box,
      linear-gradient(135deg, rgba(33, 89, 104, 0.28), rgba(167, 124, 49, 0.18), rgba(85, 119, 99, 0.18)) border-box;
    box-shadow: 0 28px 76px rgba(90, 69, 39, 0.16);
  }
  .rp-shell::before {
    background:
      radial-gradient(820px 320px at 12% 0%, rgba(33, 89, 104, 0.08), transparent 60%),
      radial-gradient(620px 280px at 88% 8%, rgba(167, 124, 49, 0.08), transparent 62%),
      radial-gradient(700px 300px at 48% 112%, rgba(85, 119, 99, 0.07), transparent 60%);
  }
  .rp-header { border-bottom-color: rgba(24, 54, 69, 0.08); }
  .rp-title {
    background: linear-gradient(90deg, #183645 0%, #215968 48%, #9a7531 100%);
    -webkit-background-clip: text;
    background-clip: text;
  }
  .rp-sub,
  .rp-pill,
  .rp-cardHint,
  .rp-state { color: rgba(69, 78, 81, 0.68); }
  .rp-pill,
  .rp-card {
    border-color: rgba(24, 54, 69, 0.08);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.80), rgba(244, 239, 230, 0.74));
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.66);
  }
  .rp-btn {
    border-color: rgba(33, 89, 104, 0.20);
    background: rgba(33, 89, 104, 0.10);
    color: #183645;
  }
  .rp-cardLabel { color: rgba(24, 54, 69, 0.72); }
  .rp-cardValue { color: #183645; }
  .rp-panel {
    border-color: rgba(33, 89, 104, 0.20);
    background:
      linear-gradient(180deg, rgba(255,255,255,0.86), rgba(244, 239, 230, 0.86)) padding-box,
      linear-gradient(135deg, rgba(33, 89, 104, 0.22), rgba(167, 124, 49, 0.16), rgba(85, 119, 99, 0.16)) border-box;
  }
  .rp-panelHeader {
    border-bottom-color: rgba(33, 89, 104, 0.14);
    background: linear-gradient(180deg, rgba(239, 234, 226, 0.96), rgba(231, 224, 212, 0.96));
  }
  .rp-panelTitle { color: #183645; }
  .rp-table { color: #283f49; }
  .rp-table th {
    color: #183645;
    background: rgba(239, 234, 226, 0.98);
    border-bottom-color: rgba(33, 89, 104, 0.14);
  }
  .rp-table th:first-child { background: rgba(239, 234, 226, 1); }
  .rp-table td { border-bottom-color: rgba(24, 54, 69, 0.07); }
  .rp-table td:first-child { background: rgba(252, 249, 243, 0.98); }
  .rp-table tbody tr:hover td { background: rgba(33, 89, 104, 0.06); }
  .rp-month { color: #183645; }
  .rp-monthDot { background: #215968; box-shadow: 0 0 18px rgba(33, 89, 104, 0.22); }
  .rp-kicker { color: #183645; }
  .rp-cellSub { color: rgba(69, 78, 81, 0.66); }
  .rp-empty { color: rgba(69, 78, 81, 0.68); }
  .rp-monthAgencyPanel {
    background:
      linear-gradient(180deg, rgba(248, 250, 252, 0.98), rgba(241, 245, 249, 0.98)) padding-box,
      linear-gradient(135deg, rgba(100, 116, 139, 0.22), rgba(203, 213, 225, 0.18)) border-box;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.10);
  }
  .rp-monthAgencyPanel .rp-panelHeader {
    background: rgba(248, 250, 252, 0.92);
    border-bottom-color: rgba(100, 116, 139, 0.16);
  }
  .rp-monthAgencyPanel .rp-panelTitle { color: #1e293b; }
  .rp-monthAgencyTable,
  .rp-monthAgencyTable th,
  .rp-monthAgencyTable td,
  .rp-monthAgencyTable th:first-child,
  .rp-monthAgencyTable td:first-child {
    color: #243244;
  }
  .rp-monthAgencyTable { --rp-agency-row-bg: #f8fafc; }
  .rp-monthAgencyTable th {
    background: rgba(241, 245, 249, 0.98);
    color: #475569;
    border-bottom-color: rgba(100, 116, 139, 0.16);
  }
  .rp-monthAgencyTable td {
    background: rgba(255, 255, 255, 0.72);
    border-top-color: rgba(100, 116, 139, 0.08);
    border-bottom-color: rgba(100, 116, 139, 0.10);
  }
  .rp-agencyDataRow td {
    color: #526174;
  }
  .rp-monthBlockCell {
    background: rgba(226, 232, 240, 0.86) !important;
    border-left-color: rgba(100, 116, 139, 0.18) !important;
    border-right-color: rgba(100, 116, 139, 0.10) !important;
  }
  .rp-monthBlockLabel,
  .rp-agencyCell { color: #1e293b; }
  .rp-agencyDataRow .rp-agencyCell { color: #475569; }
  .rp-monthTotalRow td {
    background: rgba(203, 213, 225, 0.86) !important;
    color: #1e293b;
    border-top-color: rgba(100, 116, 139, 0.22);
    border-bottom-color: rgba(100, 116, 139, 0.16);
  }
  .rp-monthTotalRow .rp-agencyCell { color: #1e293b; }
`;

function ReportsStayPerformance() {
  const { formatFromBase } = useCurrencyDisplay();
  const { themeId, languageId, language } = usePmsTheme();
  const isLightTheme = themeId === "light";
  const copy = REPORT_MESSAGES[languageId] || REPORT_MESSAGES.en;
  const textLocale = languageId === "en" ? "en-US" : language?.locale || "es-ES";
  const messageRef = useRef(copy.messages);
  const [searchParams] = useSearchParams();
  const businessUnitId = searchParams.get("business_unit_id") || "1";

  let currentUser = {};
  try {
    currentUser = JSON.parse(localStorage.getItem("palantir_user") || "{}");
  } catch (_err) {
    currentUser = {};
  }
  const isAdminRole = Number(currentUser?.id) === 1;
  const canViewReports =
    isAdminRole ||
    Boolean(currentUser?.permissions?.["reports.view"]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reservations, setReservations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [updatedAt, setUpdatedAt] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const crossTabRefreshToken = useCrossTabDataRefreshToken();

  useEffect(() => {
    messageRef.current = copy.messages;
  }, [copy.messages]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const run = async () => {
      setLoading(true);
      setError("");

      const [resTask, roomsTask] = await Promise.allSettled([
        fetchAllReservations(businessUnitId, controller.signal),
        axios.get("/api/rooms", {
          params: { business_unit_id: businessUnitId },
          signal: controller.signal,
        }),
      ]);

      if (!active) return;

      if (resTask.status === "fulfilled") {
        setReservations(Array.isArray(resTask.value) ? resTask.value : []);
      } else {
        setReservations([]);
        setError(resTask?.reason?.response?.data?.error || messageRef.current.reservationsLoadError);
      }

      setRooms(roomsTask.status === "fulfilled" && Array.isArray(roomsTask.value?.data) ? roomsTask.value.data : []);
      setUpdatedAt(new Date().toISOString());
      setLoading(false);
    };

    run().catch((err) => {
      if (!active) return;
      setError(err?.response?.data?.error || messageRef.current.loadError);
      setLoading(false);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [businessUnitId, reloadKey, crossTabRefreshToken]);

  const intFmt = useMemo(() => new Intl.NumberFormat("es-ES"), []);
  const decimalFmt = useMemo(
    () => new Intl.NumberFormat("es-ES", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    []
  );

  const report = useMemo(() => {
    const totalRooms = Array.isArray(rooms) ? rooms.length : 0;
    const todayDate = new Date();
    const todayIso = toIsoDateLocal(todayDate);
    const reportEndExclusive = addDays(todayIso, 1);
    const monthMap = new Map();
    const ensureMonth = (dateValue) => {
      const parsed = dateValue instanceof Date ? dateValue : parseDateOnly(dateValue);
      if (!parsed) return null;
      const date = firstDayOfMonth(parsed);
      const key = monthKey(date);
      if (!monthMap.has(key)) {
        monthMap.set(key, makeEmptyMonth(date, totalRooms, textLocale, todayDate));
      }
      return monthMap.get(key);
    };
    const eachStayMonth = (checkIn, checkOut, callback) => {
      const start = parseDateOnly(checkIn);
      const endExclusive = parseDateOnly(checkOut);
      if (!start || !endExclusive || endExclusive <= start) return;

      const lastNight = new Date(endExclusive);
      lastNight.setDate(lastNight.getDate() - 1);
      const cursor = firstDayOfMonth(start);
      const lastMonth = firstDayOfMonth(lastNight);

      while (cursor <= lastMonth) {
        const month = ensureMonth(cursor);
        if (month) callback(month);
        cursor.setMonth(cursor.getMonth() + 1);
      }
    };

    const activeReservations = (Array.isArray(reservations) ? reservations : []).filter((reservation) =>
      isOperational(reservation?.status)
    );
    const ensureMonthAgency = (month, label) => {
      const key = String(label || "").toLocaleLowerCase();
      if (!month.agencies.has(key)) {
        month.agencies.set(key, {
          key,
          label,
          reservations: 0,
          totalLosNights: 0,
          roomNights: 0,
          totalPrice: 0,
          occupancyPct: 0,
          avgLos: 0,
          adr: 0,
          revpar: 0,
        });
      }
      return month.agencies.get(key);
    };

    activeReservations.forEach((reservation) => {
      const checkIn = normalizeDate(reservation?.check_in_date);
      const checkOut = reservationCheckOut(reservation);
      if (!checkIn || !checkOut || checkOut <= checkIn || checkIn > todayIso) return;

      const totalNights = reservationNights(reservation);
      const revenue = toRevenue(reservation);
      const nightlyRevenue = totalNights > 0 ? revenue / totalNights : 0;
      const agencyLabel = externalAgencyLabel(reservation, copy.fallback.directHotel);
      const checkinMonth = ensureMonth(checkIn);
      const reportCheckOut = minIsoDate(checkOut, reportEndExclusive);

      if (checkinMonth) {
        checkinMonth.reservations += 1;
        checkinMonth.totalLosNights += totalNights;
        const agency = ensureMonthAgency(checkinMonth, agencyLabel);
        agency.reservations += 1;
        agency.totalLosNights += totalNights;
      }

      eachStayMonth(checkIn, reportCheckOut, (month) => {
        const overlap = overlapNights(checkIn, reportCheckOut, month.start, month.end);
        if (overlap <= 0) return;

        month.roomNights += overlap;
        month.totalPrice += nightlyRevenue * overlap;
        const agency = ensureMonthAgency(month, agencyLabel);
        agency.roomNights += overlap;
        agency.totalPrice += nightlyRevenue * overlap;
      });
    });

    const rows = Array.from(monthMap.values())
      .map((month) => {
        const monthSummary = {
          ...month,
          avgLos: month.reservations > 0 ? month.totalLosNights / month.reservations : 0,
          occupancyPct: month.capacity > 0 ? (month.roomNights / month.capacity) * 100 : 0,
          adr: month.roomNights > 0 ? month.totalPrice / month.roomNights : 0,
          revpar: month.capacity > 0 ? month.totalPrice / month.capacity : 0,
        };

        const agencyRows = Array.from(month.agencies.values())
          .filter((agency) => agency.reservations > 0 || agency.roomNights > 0 || agency.totalPrice > 0)
          .map((agency) => ({
            ...agency,
            avgLos: agency.reservations > 0 ? agency.totalLosNights / agency.reservations : 0,
            occupancyPct: month.capacity > 0 ? (agency.roomNights / month.capacity) * 100 : 0,
            adr: agency.roomNights > 0 ? agency.totalPrice / agency.roomNights : 0,
            revpar: month.capacity > 0 ? agency.totalPrice / month.capacity : 0,
          }))
          .sort(
            (a, b) =>
              b.reservations - a.reservations ||
              b.roomNights - a.roomNights ||
              b.totalPrice - a.totalPrice ||
              a.label.localeCompare(b.label, textLocale)
          );

        return {
          ...monthSummary,
          agencyRows,
        };
      })
      .filter(
        (month) =>
          month.reservations > 0 ||
          month.roomNights > 0 ||
          month.totalPrice > 0
      )
      .sort((a, b) => b.sortDate.getTime() - a.sortDate.getTime());

    const totals = rows.reduce(
      (acc, row) => {
        acc.reservations += row.reservations;
        acc.roomNights += row.roomNights;
        acc.totalLosNights += row.totalLosNights;
        acc.totalPrice += row.totalPrice;
        acc.capacity += row.capacity;
        return acc;
      },
      {
        reservations: 0,
        roomNights: 0,
        totalLosNights: 0,
        totalPrice: 0,
        capacity: 0,
      }
    );

    totals.avgLos = totals.reservations > 0 ? totals.totalLosNights / totals.reservations : 0;
    totals.occupancyPct = totals.capacity > 0 ? (totals.roomNights / totals.capacity) * 100 : 0;
    totals.adr = totals.roomNights > 0 ? totals.totalPrice / totals.roomNights : 0;
    totals.revpar = totals.capacity > 0 ? totals.totalPrice / totals.capacity : 0;

    return { rows, totals };
  }, [copy.fallback.directHotel, reservations, rooms, textLocale]);

  const formatInt = (value) => intFmt.format(Math.round(toNum(value)));
  const formatDecimal = (value) => decimalFmt.format(toNum(value));
  const formatPct = (value) => `${formatDecimal(value)}%`;
  const formatMoney = (value) => formatFromBase(toNum(value), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const formatMoneyPrecise = (value) => formatFromBase(toNum(value));
  const formatUpdated = updatedAt
    ? new Date(updatedAt).toLocaleString(textLocale, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

  if (!canViewReports) {
    return (
      <div className="rp-page">
        <style>{`${uiCss}\n${isLightTheme ? lightCss : ""}`}</style>
        <div className="rp-shell">
          <div className="rp-state">{copy.states.noPermission}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rp-page">
      <style>{`${uiCss}\n${isLightTheme ? lightCss : ""}`}</style>
      <div className="rp-shell">
        <header className="rp-header">
          <div>
            <h1 className="rp-title">{copy.title}</h1>
            <div className="rp-sub">{copy.subtitle}</div>
          </div>
          <div className="rp-actions">
            <span className="rp-pill">
              {copy.updated} {formatUpdated}
            </span>
            <button type="button" className="rp-btn" onClick={() => setReloadKey((prev) => prev + 1)}>
              <FiRefreshCw size={14} />
              {copy.refresh}
            </button>
          </div>
        </header>

        <div className="rp-body">
          {loading ? (
            <div className="rp-state">
              <div className="rp-spin" />
              {copy.states.loading}
            </div>
          ) : error ? (
            <div className="rp-state">
              <strong>{copy.states.loadErrorTitle}</strong>
              <span>{error}</span>
            </div>
          ) : (
            <>
              <section className="rp-cards">
                <div className="rp-card">
                  <div className="rp-cardHead">
                    <span className="rp-cardLabel">{copy.cards.reservations}</span>
                    <FiFileText size={16} />
                  </div>
                  <div className="rp-cardValue">{formatInt(report.totals.reservations)}</div>
                  <div className="rp-cardHint">{copy.cards.monthsWithData}</div>
                </div>
                <div className="rp-card">
                  <div className="rp-cardHead">
                    <span className="rp-cardLabel">{copy.cards.roomNights}</span>
                    <FiCalendar size={16} />
                  </div>
                  <div className="rp-cardValue">{formatInt(report.totals.roomNights)}</div>
                  <div className="rp-cardHint">{copy.cards.stayedNights}</div>
                </div>
                <div className="rp-card">
                  <div className="rp-cardHead">
                    <span className="rp-cardLabel">{copy.cards.totalPrice}</span>
                    <FiTrendingUp size={16} />
                  </div>
                  <div className="rp-cardValue">{formatMoney(report.totals.totalPrice)}</div>
                  <div className="rp-cardHint">
                    {copy.cards.adr} {formatMoneyPrecise(report.totals.adr)}
                  </div>
                </div>
                <div className="rp-card">
                  <div className="rp-cardHead">
                    <span className="rp-cardLabel">{copy.cards.averageOcc}</span>
                    <FiBarChart2 size={16} />
                  </div>
                  <div className="rp-cardValue">{formatPct(report.totals.occupancyPct)}</div>
                  <div className="rp-cardHint">{copy.cards.monthlyAverage}</div>
                </div>
                <div className="rp-card">
                  <div className="rp-cardHead">
                    <span className="rp-cardLabel">{copy.cards.revpar}</span>
                    <FiTrendingUp size={16} />
                  </div>
                  <div className="rp-cardValue">{formatMoneyPrecise(report.totals.revpar)}</div>
                  <div className="rp-cardHint">
                    {copy.cards.adr} {formatMoneyPrecise(report.totals.adr)}
                  </div>
                </div>
              </section>

              <section className="rp-panel rp-monthAgencyPanel">
                <div className="rp-panelHeader">
                  <h3 className="rp-panelTitle">
                    <FiFileText size={14} />
                    {copy.panels.externalAgencyPerformance}
                  </h3>
                </div>
                {report.rows.length === 0 ? (
                  <div className="rp-empty">{copy.empty}</div>
                ) : (
                  <div className="rp-tableWrap">
                    <table className="rp-table rp-monthAgencyTable">
                      <thead>
                        <tr>
                          <th>{copy.table.month}</th>
                          <th>{copy.table.externalAgency}</th>
                          <th>{copy.table.reservations}</th>
                          <th>{copy.table.avgLos}</th>
                          <th>{copy.table.roomNights}</th>
                          <th>{copy.table.totalPrice}</th>
                          <th>{copy.table.occupancy}</th>
                          <th>{copy.table.adr}</th>
                          <th>{copy.table.revpar}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.rows.map((month) => (
                          <React.Fragment key={month.key}>
                            {month.agencyRows.map((agency, index) => (
                              <tr key={`${month.key}-${agency.key}`} className="rp-agencyDataRow">
                                {index === 0 ? (
                                  <td className="rp-monthBlockCell" rowSpan={month.agencyRows.length + 1}>
                                    <span className="rp-monthBlockLabel">{month.label}</span>
                                  </td>
                                ) : null}
                                <td className="rp-agencyCell">{agency.label}</td>
                                <td>{formatInt(agency.reservations)}</td>
                                <td>{formatDecimal(agency.avgLos)}</td>
                                <td>{formatInt(agency.roomNights)}</td>
                                <td>{formatMoney(agency.totalPrice)}</td>
                                <td>{formatPct(agency.occupancyPct)}</td>
                                <td>{formatMoneyPrecise(agency.adr)}</td>
                                <td>{formatMoneyPrecise(agency.revpar)}</td>
                              </tr>
                            ))}
                            <tr className="rp-monthTotalRow">
                              {month.agencyRows.length === 0 ? (
                                <td className="rp-monthBlockCell">
                                  <span className="rp-monthBlockLabel">{month.label}</span>
                                </td>
                              ) : null}
                              <td className="rp-agencyCell">{copy.table.total}</td>
                              <td>{formatInt(month.reservations)}</td>
                              <td>{formatDecimal(month.avgLos)}</td>
                              <td>{formatInt(month.roomNights)}</td>
                              <td>{formatMoney(month.totalPrice)}</td>
                              <td>{formatPct(month.occupancyPct)}</td>
                              <td>{formatMoneyPrecise(month.adr)}</td>
                              <td>{formatMoneyPrecise(month.revpar)}</td>
                            </tr>
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportsStayPerformance;
